import { useEffect, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import {
  getWebSocketMessages,
  getWebSocketMessagesExportPath,
} from "@/api/admins";
import { Pagination } from "@/components/Pagination";
import { PageHeader } from "@/components/PageHeader";

function parseOptionalPositiveInt(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export function AdminRedisPage() {
  const [params, setParams] = useSearchParams();
  const [userIdInput, setUserIdInput] = useState(params.get("user_id") ?? "");
  const [keyInput, setKeyInput] = useState(params.get("key") ?? "");
  const [pageSizeInput, setPageSizeInput] = useState(params.get("page_size") ?? "50");

  useEffect(() => {
    setUserIdInput(params.get("user_id") ?? "");
    setKeyInput(params.get("key") ?? "");
    setPageSizeInput(params.get("page_size") ?? "50");
  }, [params]);

  const filters = {
    user_id: parseOptionalPositiveInt(params.get("user_id")),
    key: params.get("key") || undefined,
    page: parseOptionalPositiveInt(params.get("page")) ?? 1,
    page_size: parseOptionalPositiveInt(params.get("page_size")) ?? 50,
  };

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["admin-redis", filters],
    queryFn: () => getWebSocketMessages(filters),
  });

  function handleApplyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = new URLSearchParams();
    const resolvedUserId = parseOptionalPositiveInt(userIdInput);
    const resolvedPageSize = parseOptionalPositiveInt(pageSizeInput);
    const resolvedKey = keyInput.trim();

    if (resolvedUserId !== undefined) next.set("user_id", String(resolvedUserId));
    if (resolvedKey) next.set("key", resolvedKey);
    if (resolvedPageSize !== undefined) next.set("page_size", String(resolvedPageSize));
    setParams(next);
  }

  function handleResetFilters() {
    setUserIdInput("");
    setKeyInput("");
    setPageSizeInput("50");
    setParams(new URLSearchParams({ page_size: "50" }));
  }

  function handleDownloadBundle(userId: number, key: string) {
    window.location.assign(getWebSocketMessagesExportPath({ user_id: userId, key }));
  }

  function handlePageChange(page: number) {
    const next = new URLSearchParams(params);
    next.set("page", String(page));
    setParams(next);
  }

  return (
    <>
      <PageHeader
        title="Redis Inspector"
        lede="Inspect websocket message bundles currently stored in Redis and export the matching dataset as JSON."
        actions={
          data ? (
            <div className="stats-row">
              <article className="stat-card">
                <span>Storage</span>
                <strong>{data.storage_backend}</strong>
              </article>
              <article className="stat-card">
                <span>Visible rows</span>
                <strong>{data.total}</strong>
              </article>
            </div>
          ) : null
        }
      />

      <section className={`panel${isFetching && data ? " is-refetching" : ""}`}>
        <div className="panel-head">
          <div>
            <h2>Websocket message bundles</h2>
            <p>Filter by user or key. JSON export always downloads all matching rows.</p>
          </div>
        </div>

        <form className="redis-filter-bar" onSubmit={handleApplyFilters}>
          <div className="redis-filter-bar__header">
            <div>
              <strong>Filter Scope</strong>
              <p>Target a specific user or message key. Each row can then be downloaded as its own JSON bundle.</p>
            </div>
            <div className="redis-filter-bar__badges">
              <span className="redis-filter-bar__badge">
                {filters.user_id !== undefined ? `User ${filters.user_id}` : "All users"}
              </span>
              <span className="redis-filter-bar__badge">
                {filters.key ? `Key ${filters.key}` : "All keys"}
              </span>
            </div>
          </div>

          <div className="redis-filter-bar__grid">
            <label className="redis-filter-bar__field">
              <span className="redis-filter-bar__label">User ID</span>
              <input
                type="number"
                min="1"
                step="1"
                value={userIdInput}
                onChange={(event) => setUserIdInput(event.target.value)}
                placeholder="All users"
              />
              <small>Leave blank to scan every user bundle in Redis.</small>
            </label>
            <label className="redis-filter-bar__field redis-filter-bar__field--wide">
              <span className="redis-filter-bar__label">Message key</span>
              <input
                type="text"
                value={keyInput}
                onChange={(event) => setKeyInput(event.target.value)}
                placeholder="All keys"
              />
              <small>Use the exact websocket message key to narrow the result set.</small>
            </label>
            <label className="redis-filter-bar__field">
              <span className="redis-filter-bar__label">Page size</span>
              <input
                type="number"
                min="1"
                step="1"
                value={pageSizeInput}
                onChange={(event) => setPageSizeInput(event.target.value)}
                placeholder="50"
              />
              <small>Controls how many Redis bundles are shown on each page.</small>
            </label>
          </div>

          <div className="redis-filter-bar__actions">
            <button className="button primary" type="submit">
              Apply filters
            </button>
            <button className="button ghost" type="button" onClick={handleResetFilters}>
              Reset
            </button>
          </div>
        </form>

        {isLoading ? <p className="empty-state">Loading…</p> : null}
        {isError ? (
          <div className="callout danger">
            <strong>Could not load Redis data</strong>
            <p>{error instanceof Error ? error.message : "Unknown error"}</p>
          </div>
        ) : null}
        {!isLoading && !isError && data?.items.length === 0 ? (
          <p className="empty-state">No websocket message bundles matched the current filters.</p>
        ) : null}
        {!isLoading && !isError && data?.items.length ? (
          <>
            <div className="table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User / key</th>
                    <th>Storage key</th>
                    <th>Info history</th>
                    <th>Flight history</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.storage_key}>
                      <td>
                        <strong>User {item.user_id}</strong>
                        <small>{item.key}</small>
                      </td>
                      <td>
                        <small>{item.storage_key}</small>
                      </td>
                      <td>{item.info_history_count}</td>
                      <td>{item.flight_history_count}</td>
                      <td>
                        <button
                          className="button ghost"
                          type="button"
                          onClick={() => handleDownloadBundle(item.user_id, item.key)}
                        >
                          Download JSON
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.page}
              pageCount={Math.max(data.page_count, 1)}
              total={data.total}
              itemLabel="bundles"
              onChange={handlePageChange}
            />
          </>
        ) : null}
      </section>
    </>
  );
}
